'use client';

import { GoogleAnalytics, sendGAEvent } from '@next/third-parties/google';
import { useStyleSchema } from '@components/styleSchema/StyleSchemaHandler';
import { BRAND_CONFIGS } from '@lib/config/brandConfigs';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

declare global {
  interface Window {
    dataLayer?: Object[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Official Next.js Third-Party GA4 integration with CSP compliance
 * Following Next.js third-party libraries and CSP best practices
 */
export default function ThirdPartyGA4() {
  const { currentSchema } = useStyleSchema();
  const brand = currentSchema.name;
  const pathname = usePathname();
  
  const ga4Config = BRAND_CONFIGS[brand]?.ga4;
  
  // Load GA4 if config exists (cookie consent removed for now)
  const shouldLoadGA4 = ga4Config;

  // Initialize dataLayer with brand context on first load
  useEffect(() => {
    if (shouldLoadGA4 && ga4Config) {
      // Initialize dataLayer if not exists
      window.dataLayer = window.dataLayer || [];
      
      // Push initial brand context to dataLayer
      window.dataLayer.push({
        event: 'brand_context_loaded',
        brand: brand,
        stream_name: ga4Config.streamName,
        ...(ga4Config.streamId && { stream_id: ga4Config.streamId })
      });
    }
  }, [shouldLoadGA4, brand, ga4Config]);

  // Handle GA4 configuration
  useEffect(() => {
    if (shouldLoadGA4 && typeof window !== 'undefined' && window.gtag) {
      // Configure GA4 with brand-specific parameters
      window.gtag('config', ga4Config.measurementId, {
        custom_map: {
          brand: 'custom_brand',
          stream_name: 'custom_stream_name'
        },
        send_page_view: false // We'll handle page views manually for better control
      });
    }
  }, [shouldLoadGA4, ga4Config]);

  // Track page views using Next.js sendGAEvent for better performance
  useEffect(() => {
    if (shouldLoadGA4 && pathname && ga4Config) {
      // Use Next.js sendGAEvent instead of direct gtag calls
      sendGAEvent('page_view', {
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_location: typeof window !== 'undefined' ? window.location.href : '',
        page_path: pathname,
        custom_brand: brand,
        custom_stream_name: ga4Config.streamName
      });
    }
  }, [pathname, shouldLoadGA4, brand, ga4Config]);

  if (!shouldLoadGA4) {
    return null;
  }

  return (
    <GoogleAnalytics 
      gaId={ga4Config.measurementId}
      dataLayerName="dataLayer"
    />
  );
}